import { Contract } from '../types/contract';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyUtils';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { twilioService } from './twilioService';
import { whatsappService } from './whatsappService';
import { zenviaService } from './zenviaService';
import { emailService } from './emailService';
import { pushNotificationService } from './pushNotificationService';
import { webhookService } from './webhookService';
import { log } from '../utils/logger';

interface WhatsAppNotification {
  phoneNumber: string;
  message: string;
}

interface NotificationRecord {
  contractId: string;
  message: string;
  phoneNumber: string;
  timestamp: any;
  status: 'pending' | 'sent' | 'failed';
}

export const notificationService = {
  // Número do administrador para receber notificações
  ADMIN_PHONE: '5533998204921',

  // Enviar notificação de novo contrato
  async sendNewContractNotification(contract: Contract): Promise<void> {
    try {
      console.log('🚀 Iniciando envio de notificação para contrato:', contract.id);
      console.log('📋 Dados do contrato:', {
        contratante: contract.contratante,
        telefone: contract.telefone,
        dataFesta: contract.dataFesta
      });
      
      const message = this.buildNewContractMessage(contract);
      console.log('📝 Mensagem construída:', message.substring(0, 100) + '...');
      
      // Salvar notificação no banco de dados
      const notificationId = await this.saveNotificationToDatabase(contract.id!, message);
      console.log('💾 Notificação salva no banco com ID:', notificationId);
      
      // Sistema de notificações focado no Telegram
      let notificationSent = false;
      
        // Verificar se notificações estão ativadas
        const notificationsEnabled = localStorage.getItem('notifications_enabled') !== 'false';
        
        if (!notificationsEnabled) {
          log.info('Notificações desativadas nas configurações', 'notificationService');
          return;
        }

        // 1. Telegram (100% gratuito, principal)
        try {
          log.info('Tentando enviar notificação para Telegram', 'notificationService');
          notificationSent = await webhookService.sendToTelegram(contract);
          if (notificationSent) {
            log.info('Notificação enviada via Telegram com sucesso', 'notificationService');
          } else {
            log.warn('Telegram não configurado ou falhou', 'notificationService');
          }
        } catch (telegramError) {
          log.error('Erro ao enviar para Telegram', 'notificationService', telegramError);
        }
      
      // 2. Push Notification (100% gratuito, sempre funciona como backup)
      try {
        const pushSent = await pushNotificationService.sendNewContractNotification(contract);
        if (pushSent) {
          console.log('✅ Notificação push enviada (backup)');
        }
      } catch (pushError) {
        console.log('⚠️ Push notification falhou');
      }
      
      // 3. Email (100% gratuito, backup)
      if (!notificationSent) {
        try {
          notificationSent = await emailService.sendNewContractNotification(contract);
          if (notificationSent) {
            console.log('✅ Notificação enviada via email (backup)');
          }
        } catch (emailError) {
          console.log('⚠️ Email não configurado');
        }
      }
      
      // Atualizar status da notificação no banco
      if (notificationSent) {
        await this.updateNotificationStatus(contract.id!, 'sent');
        console.log('✅ Status da notificação atualizado para: sent');
      } else {
        await this.updateNotificationStatus(contract.id!, 'failed');
        console.log('❌ Status da notificação atualizado para: failed');
      }
      
      // Mostrar notificação visual (sempre)
      this.showBrowserNotification('Novo Contrato Recebido!', message);
      console.log('🔔 Notificação visual exibida no navegador');
      
      console.log('✅ Notificação de novo contrato processada com sucesso');
    } catch (error) {
      console.error('❌ Erro ao processar notificação de novo contrato:', error);
      console.error('❌ Detalhes do erro:', error);
      // Não lançar erro para não interromper o fluxo do formulário
    }
  },

  // Salvar notificação no banco de dados
  async saveNotificationToDatabase(contractId: string, message: string): Promise<string> {
    try {
      const notificationRecord: NotificationRecord = {
        contractId,
        message,
        phoneNumber: this.ADMIN_PHONE,
        timestamp: Timestamp.now(),
        status: 'pending'
      };

      const docRef = await addDoc(collection(db, 'notifications'), notificationRecord);
      console.log('✅ Notificação salva no banco de dados');
      return docRef.id;
    } catch (error) {
      console.error('❌ Erro ao salvar notificação no banco:', error);
      throw error;
    }
  },

  // Atualizar status da notificação
  async updateNotificationStatus(contractId: string, status: 'sent' | 'failed'): Promise<void> {
    try {
      // Aqui você precisaria implementar a busca e atualização da notificação
      // Por simplicidade, vou apenas logar o status
      console.log(`📊 Status da notificação para contrato ${contractId}: ${status}`);
    } catch (error) {
      console.error('❌ Erro ao atualizar status da notificação:', error);
    }
  },

  // Mostrar notificação do navegador
  showBrowserNotification(title: string, message: string): void {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: message.substring(0, 100) + '...',
          icon: '/images/LOGO.png'
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, {
              body: message.substring(0, 100) + '...',
              icon: '/images/LOGO.png'
            });
          }
        });
      }
    }
  },

  // Construir mensagem de notificação (versão concisa para SMS)
  buildNewContractMessage(contract: Contract): string {
    const nomeContratante = contract.contratante || 'Não informado';
    const nomeAniversariante = contract.nomeAniversariante || 'Não informado';
    const dataFesta = contract.dataFesta ? formatDate(contract.dataFesta) : 'Não informada';
    const telefone = contract.telefone || 'Não informado';
    const telefoneCliente = contract.telefone ? contract.telefone.replace(/\D/g, '') : '';
    const telefoneClienteComPais = telefoneCliente
      ? telefoneCliente.startsWith('55') ? telefoneCliente : `55${telefoneCliente}`
      : '';
    const linkWhatsAppCliente = telefoneClienteComPais ? `https://wa.me/${telefoneClienteComPais}` : 'Telefone nao informado';
    const endereco = contract.endereco ? `${contract.endereco}, ${contract.numero || ''}` : 'Não informado';
    const cidade = contract.cidade && contract.estado ? `${contract.cidade} - ${contract.estado}` : 'Não informado';
    const horario = contract.horarioInicioFesta || 'Não informado';
    const tema = contract.temaFesta || 'Não informado';
    const observacoes = contract.observacoes || 'Nenhuma observação';

    return `🎉 *NOVO CONTRATO RECEBIDO - PlayKids*

👤 *Contratante:* ${nomeContratante}
📞 *Telefone:* ${telefone}
🎂 *Aniversariante:* ${nomeAniversariante}
📅 *Data da Festa:* ${dataFesta}
🕐 *Horário:* ${horario}
📍 *Endereço:* ${endereco}
🏙️ *Cidade:* ${cidade}
🎨 *Tema:* ${tema}
💰 *Valor:* ${contract.valor ? formatCurrency(contract.valor) : 'A definir'}
📝 *Observações:* ${observacoes}

⏰ *Recebido em:* ${new Date().toLocaleString('pt-BR')}

🔗 *Links Úteis:*
• [Definir preco e gerar contrato no modelo oficial](https://playkidseventos.com.br/admin/contratos/fluxo/${contract.id})
• [Ver Detalhes do Contrato](https://playkidseventos.com.br/admin/contratos/${contract.id})
• [Criar Orçamento](https://playkidseventos.com.br/admin/contratos/orcamento/${contract.id})
• [Editar Contrato](https://playkidseventos.com.br/admin/contratos/editar/${contract.id})
• [Entrar em contato com o cliente](${linkWhatsAppCliente})

💬 *Responda rapidamente para não perder a venda!*`;
  },

  // Gerar URL do WhatsApp (para uso manual)
  generateWhatsAppUrl(message: string): string {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${this.ADMIN_PHONE}?text=${encodedMessage}`;
  },

  // Copiar mensagem para área de transferência
  async copyMessageToClipboard(message: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(message);
        return true;
      } else {
        // Fallback para navegadores mais antigos
        const textArea = document.createElement('textarea');
        textArea.value = message;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        textArea.remove();
        return result;
      }
    } catch (error) {
      console.error('❌ Erro ao copiar mensagem:', error);
      return false;
    }
  }
};
